import { insertLabel } from "@/input/utils/labels";
import { getSymbolContainingNode } from "@/models/nodes/utils";
import { isPowerOperator } from "@/models/utils/semanticChecks";

import type { DocumentModel } from "@/models/DocumentModel";
import type { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import type { NodeModel } from "@/models/nodes/types";
import type { LabelNodePosition } from "@corca/data";

export function selectedPowerValue(
  doc: DocumentModel,
): { power: OperatorNodeModel; count: number } | null {
  const inline = doc.cursor.node;
  const power = inline?.parent;
  const { selectedNodes } = doc.selection;

  if (!inline || !isPowerOperator(power)) {
    return null;
  }

  if (selectedNodes.length === 0 || selectedNodes[0].index !== 0) {
    return null;
  }

  return { power, count: selectedNodes.length };
}

function leadingValueNodes(power: OperatorNodeModel, count: number): NodeModel[] {
  const [inline] = power.parameters;
  return inline ? inline.nodes.slice(0, count) : [];
}

function removeEmptyPower(power: OperatorNodeModel) {
  const [inline] = power.parameters;

  if (inline && inline.nodes.length === 0) {
    power.doc.removeNode(power);
  }
}

export function demotePower(power: OperatorNodeModel, count: number) {
  const { doc } = power;

  doc.transact(() => {
    for (const node of leadingValueNodes(power, count)) {
      power.insertSiblingBefore(node);
    }

    removeEmptyPower(power);
    doc.selection.resetSelection();
  }, "demotePower");
}

export function movePowerValueToLabel(
  power: OperatorNodeModel,
  count: number,
  position: LabelNodePosition = "right-top",
) {
  const { doc } = power;
  const symbolContainer = getSymbolContainingNode(power);

  if (!symbolContainer) {
    return;
  }

  doc.transact(() => {
    insertLabel(symbolContainer, position, false);

    const label = symbolContainer.symbolNode?.labelNodes.find(
      (node) => node.props.position === position,
    );

    label?.inlineNode?.appendChildren(leadingValueNodes(power, count));
    removeEmptyPower(power);
    doc.selection.resetSelection();
  }, "movePowerValueToLabel");
}
